import {
    cloneChildNodes,
    dispatchValueEvents,
    getStringAttribute,
    normalizeSearchText,
    reconcilePickerPanel,
    resolveSearchResultsScrollHost,
    resolveScopedUrl,
    searchResultsAreNearEnd,
    setStringAttribute,
    upgradeProperty,
} from "./searchable-picker-core.js";

const CLOSE_DELAY_MS = 150;
const LOCAL_RESULT_LIMIT = 24;
const OFFSET_QUERY_PARAM = "offset";
const SEARCH_QUERY_PARAM = "q";
const SELECTED_QUERY_PARAM = "selected";

function parseHtmlFragment(html) {
    const template = document.createElement("template");
    template.innerHTML = String(html ?? "");
    return template.content;
}

function uniqueValues(values) {
    const seen = new Set();
    const out = [];
    for (const value of Array.isArray(values) ? values : []) {
        const normalized = String(value ?? "").trim();
        if (!normalized || seen.has(normalized)) {
            continue;
        }
        seen.add(normalized);
        out.push(normalized);
    }
    return out;
}

export class FishySearchableMultiselect extends HTMLElement {
    static get observedAttributes() {
        return ["placeholder", "search-url", "search-url-root"];
    }

    constructor() {
        super();
        this._closeTimer = 0;
        this._activeSearchQuery = "";
        this._activeSearchSelectedValues = [];
        this._loadMoreController = null;
        this._lastSearchKey = "";
        this._outsideListenerAttached = false;
        this._panelAnchor = null;
        this._panelDetached = false;
        this._panelElement = null;
        this._panelEventsAttached = false;
        this._panelPositionFrame = 0;
        this._resultsElement = null;
        this._resultsScrollHost = null;
        this._searchController = null;
        this._selectionHydrationController = null;
        this._selectionValues = [];
        this._valueInitialized = false;
        this._viewportListenersAttached = false;

        this._handleClick = this._handleClick.bind(this);
        this._handleDocumentPointerDown = this._handleDocumentPointerDown.bind(this);
        this._handleFocusIn = this._handleFocusIn.bind(this);
        this._handleFocusOut = this._handleFocusOut.bind(this);
        this._handleInput = this._handleInput.bind(this);
        this._handleKeyDown = this._handleKeyDown.bind(this);
        this._handleMouseDown = this._handleMouseDown.bind(this);
        this._handlePointerDown = this._handlePointerDown.bind(this);
        this._handleResultsScroll = this._handleResultsScroll.bind(this);
        this._handleViewportChange = this._handleViewportChange.bind(this);

        this.addEventListener("click", this._handleClick);
        this.addEventListener("focusin", this._handleFocusIn);
        this.addEventListener("focusout", this._handleFocusOut);
        this.addEventListener("input", this._handleInput);
        this.addEventListener("keydown", this._handleKeyDown);
        this.addEventListener("mousedown", this._handleMouseDown);
        this.addEventListener("pointerdown", this._handlePointerDown);
    }

    get placeholder() {
        return getStringAttribute(this, "placeholder");
    }

    set placeholder(value) {
        setStringAttribute(this, "placeholder", value);
    }

    get searchUrl() {
        return getStringAttribute(this, "search-url");
    }

    set searchUrl(value) {
        setStringAttribute(this, "search-url", value);
    }

    get searchUrlRoot() {
        return getStringAttribute(this, "search-url-root");
    }

    set searchUrlRoot(value) {
        setStringAttribute(this, "search-url-root", value);
    }

    get value() {
        return [...this._selectionValues];
    }

    set value(values) {
        this._setSelectionValues(values, { resolveMissingRemote: true });
    }

    connectedCallback() {
        upgradeProperty(this, "placeholder");
        upgradeProperty(this, "searchUrl");
        upgradeProperty(this, "searchUrlRoot");
        upgradeProperty(this, "value");
        if (!this._valueInitialized) {
            this._selectionValues = uniqueValues(
                Array.from(
                    this.querySelectorAll("[data-searchable-multiselect-remove]"),
                    (button) => button.getAttribute("data-value"),
                ),
            );
            this._valueInitialized = true;
        }
        this._ensurePanelReference();
        this._attachResultsEvents();
        queueMicrotask(() => {
            if (!this.isConnected) {
                return;
            }
            this._syncUi();
            this._setSelectionValues(this._selectionValues, { resolveMissingRemote: true });
            if (!this.searchUrl) {
                this.search(this.searchInputElement()?.value ?? "");
            }
        });
    }

    disconnectedCallback() {
        this.close();
        this._detachPanelEvents();
        this._cancelClose();
        this._abortSearch();
        this._abortLoadMore();
        this._abortSelectionHydration();
        this._detachResultsEvents();
        this._detachOutsideListener();
        this._detachViewportListeners();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue) {
            return;
        }
        if (name === "placeholder") {
            this._syncUi();
            return;
        }
        if (name === "search-url" || name === "search-url-root") {
            this._lastSearchKey = "";
            if (this.isOpen()) {
                this.search(this.searchInputElement()?.value ?? "");
            }
        }
    }

    open() {
        const panel = this.panelElement();
        if (!(panel instanceof HTMLElement)) {
            return;
        }
        this._cancelClose();
        panel.hidden = false;
        this.style.zIndex = "60";
        this._attachOutsideListener();
        let detachedPanelWidth = 0;
        if (this._usesDetachedPanel()) {
            detachedPanelWidth = this._measurePanelRect().width;
            this._detachPanel();
            this._attachPanelEvents();
            this._attachViewportListeners();
            this._positionPanel(detachedPanelWidth);
        }
        this._attachResultsEvents();
        this.search(this.searchInputElement()?.value ?? "");
        window.requestAnimationFrame(() => {
            if (!this.isConnected || !this.isOpen()) {
                return;
            }
            if (this._usesDetachedPanel()) {
                this._positionPanel(detachedPanelWidth);
            }
        });
    }

    close() {
        this._cancelClose();
        const panel = this.panelElement();
        if (panel instanceof HTMLElement) {
            panel.hidden = true;
        }
        this.style.zIndex = "";
        this._abortSearch();
        this._abortLoadMore();
        this._lastSearchKey = "";
        this.resultsElement()?.removeAttribute?.("aria-busy");
        this._detachOutsideListener();
        if (this._usesDetachedPanel()) {
            this._detachViewportListeners();
            this._restorePanel();
            this._detachPanelEvents();
        }
    }

    isOpen() {
        const panel = this.panelElement();
        return panel instanceof HTMLElement ? !panel.hidden : false;
    }

    panelElement() {
        this._ensurePanelReference();
        return this._panelElement;
    }

    resultsElement() {
        return this.panelElement()?.querySelector?.('[data-role="results"]') ?? null;
    }

    searchInputElement() {
        return this.querySelector('[data-role="search-input"]');
    }

    shellElement() {
        return this.querySelector('[data-role="shell"]');
    }

    selectionElement() {
        return this.querySelector('[data-role="selection"]');
    }

    catalogTemplates() {
        return Array.from(
            this.querySelectorAll('template[data-role="option-template"]'),
        ).filter((element) => element instanceof HTMLTemplateElement);
    }

    search(rawQuery) {
        const query = String(rawQuery ?? "");
        const selectedValues = this.selectedValues();
        if (this.searchUrl && !this.isOpen()) {
            this._lastSearchKey = "";
            return;
        }
        const searchKey = `${selectedValues.join("\n")}\n${query}`;
        if (this._lastSearchKey === searchKey) {
            return;
        }
        this._abortSearch();
        this._abortLoadMore();
        this._lastSearchKey = searchKey;
        this._activeSearchQuery = query;
        this._activeSearchSelectedValues = selectedValues;
        const searchUrl = this._buildSearchUrl(query, selectedValues);
        if (!searchUrl) {
            this._renderLocalResults(query, selectedValues);
            return;
        }

        const results = this.resultsElement();
        if (!(results instanceof HTMLElement)) {
            return;
        }
        results.setAttribute("aria-busy", "true");
        const controller = new AbortController();
        this._searchController = controller;
        fetch(searchUrl, {
            cache: "no-store",
            headers: {
                Accept: "text/html",
            },
            signal: controller.signal,
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Search request failed: ${response.status}`);
                }
                return response.text();
            })
            .then((html) => {
                if (controller.signal.aborted) {
                    return;
                }
                const currentResults = this.resultsElement();
                if (!(currentResults instanceof HTMLElement)) {
                    return;
                }
                const fragment = parseHtmlFragment(html);
                const nextResults = fragment.firstElementChild;
                if (!(nextResults instanceof HTMLElement)) {
                    return;
                }
                currentResults.replaceWith(nextResults);
                this._attachResultsEvents();
                this._positionOpenDetachedPanel();
            })
            .catch((error) => {
                if (error?.name === "AbortError") {
                    return;
                }
                console.error("Error loading searchable multiselect results:", error);
            })
            .finally(() => {
                if (this._searchController === controller) {
                    this.resultsElement()?.removeAttribute?.("aria-busy");
                    this._searchController = null;
                }
            });
    }

    selectedValues() {
        return this.value;
    }

    select(value) {
        const template = this._findCatalogTemplateByValue(value);
        if (!(template instanceof HTMLTemplateElement)) {
            return;
        }

        const current = this.selectedValues();
        const selectionGroupKey = getStringAttribute(template, "data-selection-group-key");
        if (!selectionGroupKey) {
            console.error(`Searchable multiselect option ${value} has no selection group`);
            return;
        }
        const next = current.filter((candidate) => {
            const candidateTemplate = this._findCatalogTemplateByValue(candidate);
            return candidate === value
                || !(candidateTemplate instanceof HTMLTemplateElement)
                || getStringAttribute(candidateTemplate, "data-selection-group-key")
                    !== selectionGroupKey;
        });
        if (!next.includes(value)) {
            next.push(value);
        }

        if (
            next.length === current.length
            && next.every((candidate, index) => candidate === current[index])
        ) {
            this._clearSearch();
            return;
        }

        this._setSelectionValues(next, { refreshOpenSearch: false });
        dispatchValueEvents(this);
        this._clearSearch();
        this.search("");

        const searchInput = this.searchInputElement();
        if (searchInput instanceof HTMLInputElement) {
            searchInput.focus();
        }
    }

    remove(value) {
        const current = this.selectedValues();
        const next = current.filter((candidate) => candidate !== value);
        if (next.length === current.length) {
            return;
        }

        this._setSelectionValues(next, { refreshOpenSearch: false });
        dispatchValueEvents(this);
        this._lastSearchKey = "";
        this.search(this.searchInputElement()?.value ?? "");
    }

    _attachOutsideListener() {
        if (this._outsideListenerAttached) {
            return;
        }
        document.addEventListener("pointerdown", this._handleDocumentPointerDown, true);
        this._outsideListenerAttached = true;
    }

    _abortSearch() {
        if (!this._searchController) {
            return;
        }
        this._searchController.abort();
        this._searchController = null;
    }

    _abortLoadMore() {
        if (!this._loadMoreController) {
            return;
        }
        this._loadMoreController.abort();
        this._loadMoreController = null;
    }

    _abortSelectionHydration() {
        if (!this._selectionHydrationController) {
            return;
        }
        this._selectionHydrationController.abort();
        this._selectionHydrationController = null;
    }

    _buildSearchUrl(query, selectedValues, offset = null) {
        const resolved = resolveScopedUrl(this.searchUrl, this.searchUrlRoot);
        if (!resolved) {
            return "";
        }

        let url;
        try {
            url = new URL(resolved, window.location.href);
        } catch (_) {
            return "";
        }

        const normalizedQuery = String(query ?? "").trim();
        if (normalizedQuery) {
            url.searchParams.set(SEARCH_QUERY_PARAM, normalizedQuery);
        } else {
            url.searchParams.delete(SEARCH_QUERY_PARAM);
        }

        const selected = uniqueValues(selectedValues).join(",");
        if (selected) {
            url.searchParams.set(SELECTED_QUERY_PARAM, selected);
        } else {
            url.searchParams.delete(SELECTED_QUERY_PARAM);
        }

        if (Number.isInteger(offset) && offset >= 0) {
            url.searchParams.set(OFFSET_QUERY_PARAM, String(offset));
        } else {
            url.searchParams.delete(OFFSET_QUERY_PARAM);
        }

        const calculatorOwner = this.closest?.("[data-calculator-source-reference]");
        const sourceReference = String(
            calculatorOwner?.getAttribute?.("data-calculator-source-reference") ?? "",
        ).trim();
        if (sourceReference) {
            url.searchParams.set("ref", sourceReference);
        }
        return url.toString();
    }

    _attachResultsEvents() {
        const results = this.resultsElement();
        const scrollHost = resolveSearchResultsScrollHost(results, this.panelElement());
        if (this._resultsElement === results && this._resultsScrollHost === scrollHost) {
            return;
        }
        this._detachResultsEvents();
        if (!(results instanceof HTMLElement) || !(scrollHost instanceof HTMLElement)) {
            return;
        }
        scrollHost.addEventListener("scroll", this._handleResultsScroll, { passive: true });
        this._resultsElement = results;
        this._resultsScrollHost = scrollHost;
    }

    _attachPanelEvents() {
        const panel = this.panelElement();
        if (!(panel instanceof HTMLElement) || this._panelEventsAttached) {
            return;
        }
        panel.addEventListener("click", this._handleClick);
        panel.addEventListener("focusin", this._handleFocusIn);
        panel.addEventListener("focusout", this._handleFocusOut);
        panel.addEventListener("input", this._handleInput);
        panel.addEventListener("keydown", this._handleKeyDown);
        panel.addEventListener("mousedown", this._handleMouseDown);
        panel.addEventListener("pointerdown", this._handlePointerDown);
        this._panelEventsAttached = true;
    }

    _attachViewportListeners() {
        if (this._viewportListenersAttached) {
            return;
        }
        window.addEventListener("resize", this._handleViewportChange);
        window.addEventListener("scroll", this._handleViewportChange, true);
        window.visualViewport?.addEventListener?.("resize", this._handleViewportChange);
        window.visualViewport?.addEventListener?.("scroll", this._handleViewportChange);
        this._viewportListenersAttached = true;
    }

    _cancelClose() {
        if (!this._closeTimer) {
            return;
        }
        window.clearTimeout(this._closeTimer);
        this._closeTimer = 0;
    }

    _clearSearch() {
        const searchInput = this.searchInputElement();
        if (searchInput instanceof HTMLInputElement) {
            searchInput.value = "";
        }
        this._lastSearchKey = "";
    }

    _clearDetachedPanelStyles() {
        const panel = this.panelElement();
        if (!(panel instanceof HTMLElement)) {
            return;
        }
        panel.style.position = "";
        panel.style.left = "";
        panel.style.top = "";
        panel.style.width = "";
        panel.style.minWidth = "";
        panel.style.maxWidth = "";
        panel.style.maxHeight = "";
        panel.style.overscrollBehavior = "";
        panel.style.zIndex = "";
        panel.style.margin = "";
        this._clearDetachedResultsStyles();
    }

    _clearDetachedResultsStyles() {
        const results = this.resultsElement();
        if (!(results instanceof HTMLElement)) {
            return;
        }
        results.style.maxHeight = "";
        results.style.overflowY = "";
        results.style.overscrollBehavior = "";
    }

    _configuredPanelWidthStyle() {
        const width = getStringAttribute(this, "panel-width");
        if (!width) {
            return "";
        }
        return `min(${width}, calc(100vw - 24px))`;
    }

    _detachOutsideListener() {
        if (!this._outsideListenerAttached) {
            return;
        }
        document.removeEventListener("pointerdown", this._handleDocumentPointerDown, true);
        this._outsideListenerAttached = false;
    }

    _detachPanel() {
        const panel = this.panelElement();
        const body = document.body || document.documentElement;
        if (!(panel instanceof HTMLElement) || !(body instanceof HTMLElement) || this._panelDetached) {
            return;
        }
        if (!(this._panelAnchor instanceof Node) && panel.parentNode) {
            this._panelAnchor = document.createComment("fishy-searchable-multiselect-panel");
            panel.parentNode.insertBefore(this._panelAnchor, panel);
        }
        body.appendChild(panel);
        this._panelDetached = true;
    }

    _detachPanelEvents() {
        const panel = this.panelElement();
        if (!(panel instanceof HTMLElement) || !this._panelEventsAttached) {
            return;
        }
        panel.removeEventListener("click", this._handleClick);
        panel.removeEventListener("focusin", this._handleFocusIn);
        panel.removeEventListener("focusout", this._handleFocusOut);
        panel.removeEventListener("input", this._handleInput);
        panel.removeEventListener("keydown", this._handleKeyDown);
        panel.removeEventListener("mousedown", this._handleMouseDown);
        panel.removeEventListener("pointerdown", this._handlePointerDown);
        this._panelEventsAttached = false;
    }

    _detachResultsEvents() {
        if (this._resultsScrollHost instanceof HTMLElement) {
            this._resultsScrollHost.removeEventListener("scroll", this._handleResultsScroll);
        }
        this._resultsElement = null;
        this._resultsScrollHost = null;
    }

    _detachViewportListeners() {
        if (!this._viewportListenersAttached) {
            return;
        }
        window.removeEventListener("resize", this._handleViewportChange);
        window.removeEventListener("scroll", this._handleViewportChange, true);
        window.visualViewport?.removeEventListener?.("resize", this._handleViewportChange);
        window.visualViewport?.removeEventListener?.("scroll", this._handleViewportChange);
        this._viewportListenersAttached = false;
        if (this._panelPositionFrame) {
            window.cancelAnimationFrame?.(this._panelPositionFrame);
            this._panelPositionFrame = 0;
        }
    }

    _ensurePanelReference() {
        const panel = reconcilePickerPanel(this, this._panelElement, this._panelDetached);
        if (panel !== this._panelElement) {
            this._detachResultsEvents();
            this._panelElement = panel;
        }
        if (
            this._panelElement instanceof HTMLElement
            && !this._panelElement.hasAttribute("data-searchable-multiselect-panel")
        ) {
            this._panelElement.setAttribute("data-searchable-multiselect-panel", "");
        }
        return this._panelElement;
    }

    _findCatalogTemplateByValue(value) {
        return this.catalogTemplates().find(
            (template) => template.getAttribute("data-value") === value,
        ) ?? null;
    }

    _findFirstAddableResultButton() {
        return (
            this.resultsElement()?.querySelector(
                "button[data-searchable-multiselect-option]:not([data-selected='true'])",
            ) ?? null
        );
    }

    _handleClick(event) {
        if (!(event.target instanceof Element)) {
            return;
        }

        const removeButton = event.target.closest(
            "button[data-searchable-multiselect-remove]",
        );
        if (removeButton && this.contains(removeButton)) {
            event.preventDefault();
            this.remove(String(removeButton.getAttribute("data-value") ?? ""));
            return;
        }

        const option = event.target.closest(
            "button[data-searchable-multiselect-option]",
        );
        if (option && this._ownsNode(option)) {
            event.preventDefault();
            if (option.getAttribute("data-selected") === "true") {
                this._clearSearch();
                this.search("");
                return;
            }
            this._importRemoteOption(option);
            this.select(String(option.getAttribute("data-value") ?? ""));
            return;
        }

        const shell = event.target.closest('[data-role="shell"]');
        if (!shell || !this._ownsNode(shell)) {
            return;
        }

        const searchInput = this.searchInputElement();
        if (searchInput instanceof HTMLInputElement && event.target !== searchInput) {
            searchInput.focus();
        }
        this.open();
    }

    _handleDocumentPointerDown(event) {
        if (!(event.target instanceof Node) || this._ownsNode(event.target)) {
            return;
        }
        this.close();
    }

    _handleFocusIn(event) {
        if (!this._ownsNode(event.target)) {
            return;
        }
        this._cancelClose();
        if (event.target === this.searchInputElement()) {
            this.open();
        }
    }

    _handleFocusOut(event) {
        if (!this.isOpen()) {
            return;
        }

        const nextTarget = event.relatedTarget;
        if (nextTarget instanceof Node && this._ownsNode(nextTarget)) {
            return;
        }

        this._scheduleClose();
    }

    _handleInput(event) {
        if (event.target !== this.searchInputElement()) {
            return;
        }
        if (!this.isOpen()) {
            this.open();
            return;
        }
        this.search(event.target.value);
    }

    _handleKeyDown(event) {
        if (!(event.target instanceof Element) || !this._ownsNode(event.target)) {
            return;
        }

        if (event.key === "Escape") {
            event.preventDefault();
            this.close();
            this.searchInputElement()?.blur();
            return;
        }

        if (event.key !== "Enter" || event.target !== this.searchInputElement()) {
            return;
        }

        const option = this._findFirstAddableResultButton();
        if (!(option instanceof HTMLButtonElement)) {
            return;
        }
        event.preventDefault();
        this._importRemoteOption(option);
        this.select(String(option.getAttribute("data-value") ?? ""));
    }

    _handleMouseDown(event) {
        this._handlePressStart(event);
    }

    _handlePointerDown(event) {
        this._handlePressStart(event);
    }

    _handleResultsScroll() {
        this._maybeLoadMore();
    }

    _handlePressStart(event) {
        if (!(event.target instanceof Element) || !this._ownsNode(event.target)) {
            return;
        }

        this._cancelClose();

        if (
            event.target.closest("button[data-searchable-multiselect-option]")
            || event.target.closest("button[data-searchable-multiselect-remove]")
        ) {
            event.preventDefault();
        }
    }

    _handleViewportChange(event = null) {
        if (event?.type === "scroll" && event.target instanceof Node) {
            const panel = this.panelElement();
            if (panel instanceof Node && panel.contains(event.target)) {
                return;
            }
        }
        if (!this.isOpen() || this._panelPositionFrame) {
            return;
        }
        this._panelPositionFrame = window.requestAnimationFrame(() => {
            this._panelPositionFrame = 0;
            this._positionPanel();
        }) || 0;
    }

    _maybeLoadMore() {
        const results = this.resultsElement();
        if (!(results instanceof HTMLElement) || this._searchController || this._loadMoreController) {
            return;
        }
        const offset = this._resultsNextOffset(results);
        if (offset === null) {
            return;
        }
        const scrollHost = this._resultsScrollHost
            ?? resolveSearchResultsScrollHost(results, this.panelElement());
        if (!searchResultsAreNearEnd(scrollHost)) {
            return;
        }
        this._loadMoreRemote(offset);
    }

    _loadMoreRemote(offset) {
        const searchUrl = this._buildSearchUrl(
            this._activeSearchQuery,
            this._activeSearchSelectedValues,
            offset,
        );
        if (!searchUrl) {
            return;
        }
        const results = this.resultsElement();
        if (!(results instanceof HTMLElement)) {
            return;
        }
        results.setAttribute("aria-busy", "true");
        const controller = new AbortController();
        this._loadMoreController = controller;
        fetch(searchUrl, {
            cache: "no-store",
            headers: { Accept: "text/html" },
            signal: controller.signal,
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Search request failed: ${response.status}`);
                }
                return response.text();
            })
            .then((html) => {
                if (controller.signal.aborted) {
                    return;
                }
                const currentResults = this.resultsElement();
                if (!(currentResults instanceof HTMLElement)) {
                    return;
                }
                const fragment = parseHtmlFragment(html);
                const nextResults = fragment.firstElementChild;
                if (!(nextResults instanceof HTMLElement)) {
                    return;
                }
                currentResults.append(...Array.from(nextResults.childNodes));
                this._setResultsNextOffset(
                    currentResults,
                    this._resultsNextOffset(nextResults),
                );
                this._attachResultsEvents();
                this._positionOpenDetachedPanel();
            })
            .catch((error) => {
                if (error?.name !== "AbortError") {
                    console.error("Error loading more searchable multiselect results:", error);
                }
            })
            .finally(() => {
                results.removeAttribute("aria-busy");
                if (this._loadMoreController === controller) {
                    this._loadMoreController = null;
                }
            });
    }

    _resultsNextOffset(results = this.resultsElement()) {
        const raw = String(results?.getAttribute?.("data-next-offset") ?? "").trim();
        if (!raw) {
            return null;
        }
        const value = Number.parseInt(raw, 10);
        return Number.isInteger(value) && value >= 0 ? value : null;
    }

    _setResultsNextOffset(results, nextOffset) {
        if (!(results instanceof HTMLElement)) {
            return;
        }
        if (Number.isInteger(nextOffset) && nextOffset >= 0) {
            results.setAttribute("data-next-offset", String(nextOffset));
        } else {
            results.removeAttribute("data-next-offset");
        }
    }

    _localTemplateMatches(template, normalizedQuery) {
        if (!normalizedQuery) {
            return true;
        }
        const haystack = normalizeSearchText(
            [
                template.getAttribute("data-search-text"),
                template.getAttribute("data-label"),
                template.textContent,
            ]
                .filter(Boolean)
                .join(" "),
        );
        return normalizedQuery
            .split(/\s+/)
            .filter(Boolean)
            .every((part) => haystack.includes(part));
    }

    _importRemoteOption(button) {
        if (!(button instanceof HTMLElement)) {
            return;
        }
        const value = String(button.getAttribute("data-value") ?? "").trim();
        if (!value || this._findCatalogTemplateByValue(value)) {
            return;
        }

        const label = String(button.getAttribute("data-label") ?? value).trim();
        const selectionGroupKey = getStringAttribute(button, "data-selection-group-key");

        const catalog = this.querySelector('[data-role="catalog"]');
        const content = button.querySelector('[data-role="option-content"]');
        if (!(catalog instanceof HTMLElement) || !(content instanceof HTMLElement)) {
            return;
        }
        const template = document.createElement("template");
        template.setAttribute("data-role", "option-template");
        template.setAttribute("data-value", value);
        template.setAttribute("data-label", label);
        template.setAttribute(
            "data-search-text",
            String(button.textContent ?? label).trim(),
        );
        if (selectionGroupKey) {
            template.setAttribute("data-selection-group-key", selectionGroupKey);
        }
        template.content.append(...cloneChildNodes(content));
        catalog.append(template);
    }

    _setSelectionValues(values, {
        refreshOpenSearch = true,
        resolveMissingRemote = false,
    } = {}) {
        const desiredValues = uniqueValues(values);
        this._selectionValues = desiredValues;
        this._valueInitialized = true;
        this._abortSelectionHydration();
        this._syncSelection();
        this._lastSearchKey = "";
        if (refreshOpenSearch && this.isOpen()) {
            this.search(this.searchInputElement()?.value ?? "");
        }
        if (!resolveMissingRemote) {
            return;
        }

        const missingValues = desiredValues.filter(
            (value) => !this._findCatalogTemplateByValue(value),
        );
        if (!missingValues.length) {
            return;
        }

        const controller = new AbortController();
        this._selectionHydrationController = controller;
        Promise.all(missingValues.map(async (value) => {
            const searchUrl = this._buildSearchUrl(value, desiredValues);
            if (!searchUrl) {
                throw new Error(`No remote catalog is available for selected value ${value}`);
            }
            const response = await fetch(searchUrl, {
                cache: "no-store",
                headers: { Accept: "text/html" },
                signal: controller.signal,
            });
            if (!response.ok) {
                throw new Error(`Selected option request failed: ${response.status}`);
            }
            const fragment = parseHtmlFragment(await response.text());
            return Array.from(
                fragment.querySelectorAll?.("[data-searchable-multiselect-option]") ?? [],
            ).find((button) => getStringAttribute(button, "data-value") === value) ?? null;
        }))
            .then((buttons) => {
                if (
                    controller.signal.aborted
                    || desiredValues.length !== this._selectionValues.length
                    || desiredValues.some((value, index) => value !== this._selectionValues[index])
                ) {
                    return;
                }
                for (const button of buttons) {
                    this._importRemoteOption(button);
                }
                const unresolved = missingValues.filter(
                    (value) => !this._findCatalogTemplateByValue(value),
                );
                if (unresolved.length) {
                    throw new Error(`Selected options are unavailable: ${unresolved.join(", ")}`);
                }
                this._syncSelection();
            })
            .catch((error) => {
                if (error?.name !== "AbortError") {
                    console.error("Error resolving searchable multiselect selection:", error);
                }
            })
            .finally(() => {
                if (this._selectionHydrationController === controller) {
                    this._selectionHydrationController = null;
                }
            });
    }

    _renderLocalResults(rawQuery, selectedValues) {
        const results = this.resultsElement();
        if (!(results instanceof HTMLElement)) {
            return;
        }

        const selectedValueSet = new Set(selectedValues);
        const normalizedQuery = normalizeSearchText(rawQuery);
        const matches = this.catalogTemplates()
            .filter((template) => this._localTemplateMatches(template, normalizedQuery))
            .sort((left, right) => {
                const leftValue = String(left.getAttribute("data-value") ?? "");
                const rightValue = String(right.getAttribute("data-value") ?? "");
                const leftSelected = selectedValueSet.has(leftValue) ? 1 : 0;
                const rightSelected = selectedValueSet.has(rightValue) ? 1 : 0;
                if (leftSelected !== rightSelected) {
                    return leftSelected - rightSelected;
                }
                const leftLabel = String(left.getAttribute("data-label") ?? leftValue);
                const rightLabel = String(right.getAttribute("data-label") ?? rightValue);
                return leftLabel.localeCompare(rightLabel);
            })
            .slice(0, LOCAL_RESULT_LIMIT);

        if (!matches.length) {
            const item = document.createElement("li");
            item.className = "menu-disabled";
            const label = document.createElement("span");
            label.textContent = "No matching options";
            item.append(label);
            results.replaceChildren(item);
            this._positionOpenDetachedPanel();
            return;
        }

        results.replaceChildren(
            ...matches.map((template) => {
                const value = String(template.getAttribute("data-value") ?? "");
                const label = String(template.getAttribute("data-label") ?? value).trim();
                const isSelected = selectedValueSet.has(value);
                const item = document.createElement("li");
                const button = document.createElement("button");
                button.type = "button";
                button.className = `justify-between gap-3 text-left${isSelected ? " opacity-75" : ""}`;
                button.dataset.searchableMultiselectOption = "";
                button.setAttribute("data-value", value);
                button.setAttribute("data-label", label);
                button.setAttribute("data-selected", isSelected ? "true" : "false");
                const selectionGroupKey = getStringAttribute(
                    template,
                    "data-selection-group-key",
                );
                if (selectionGroupKey) {
                    button.setAttribute("data-selection-group-key", selectionGroupKey);
                }

                const optionContent = document.createElement("span");
                optionContent.className = "flex min-w-0 flex-1 items-center gap-3";
                optionContent.append(...cloneChildNodes(template.content));
                button.append(optionContent);

                if (isSelected) {
                    const badge = document.createElement("span");
                    badge.className = "badge badge-soft badge-primary badge-xs";
                    badge.textContent = "Added";
                    button.append(badge);
                }

                item.append(button);
                return item;
            }),
        );
        this._positionOpenDetachedPanel();
    }

    _measurePanelRect() {
        const panel = this.panelElement();
        if (!(panel instanceof HTMLElement)) {
            return { width: 0, height: 0 };
        }
        const previousHidden = panel.hidden;
        const previousVisibility = panel.style.visibility;
        const previousWidth = panel.style.width;
        const previousMinWidth = panel.style.minWidth;
        const previousMaxWidth = panel.style.maxWidth;
        const previousMaxHeight = panel.style.maxHeight;
        const previousOverscrollBehavior = panel.style.overscrollBehavior;
        const results = this.resultsElement();
        const previousResultsMaxHeight = results instanceof HTMLElement
            ? results.style.maxHeight
            : "";
        const previousResultsOverflowY = results instanceof HTMLElement
            ? results.style.overflowY
            : "";
        const previousResultsOverscrollBehavior = results instanceof HTMLElement
            ? results.style.overscrollBehavior
            : "";
        const configuredWidth = this._configuredPanelWidthStyle();
        panel.hidden = false;
        panel.style.visibility = "hidden";
        panel.style.maxHeight = "";
        panel.style.overscrollBehavior = "";
        if (results instanceof HTMLElement) {
            results.style.maxHeight = "";
            results.style.overflowY = "";
            results.style.overscrollBehavior = "";
        }
        if (configuredWidth) {
            panel.style.width = configuredWidth;
            panel.style.minWidth = "0";
            panel.style.maxWidth = "calc(100vw - 24px)";
        }
        const rect = panel.getBoundingClientRect();
        panel.hidden = previousHidden;
        panel.style.visibility = previousVisibility;
        panel.style.width = previousWidth;
        panel.style.minWidth = previousMinWidth;
        panel.style.maxWidth = previousMaxWidth;
        panel.style.maxHeight = previousMaxHeight;
        panel.style.overscrollBehavior = previousOverscrollBehavior;
        if (results instanceof HTMLElement) {
            results.style.maxHeight = previousResultsMaxHeight;
            results.style.overflowY = previousResultsOverflowY;
            results.style.overscrollBehavior = previousResultsOverscrollBehavior;
        }
        return rect;
    }

    _ownsNode(node) {
        if (!(node instanceof Node)) {
            return false;
        }
        const panel = this.panelElement();
        return this.contains(node) || (panel instanceof Node && panel.contains(node));
    }

    _positionOpenDetachedPanel() {
        if (!this.isOpen() || !this._usesDetachedPanel()) {
            return;
        }
        this._positionPanel();
    }

    _positionPanel(measuredWidth = 0) {
        const panel = this.panelElement();
        const anchor = this.shellElement();
        if (!(panel instanceof HTMLElement) || !(anchor instanceof HTMLElement)) {
            return;
        }
        const viewportHeight = Math.max(window.innerHeight || 0, 240);
        const visualViewportLeft = window.visualViewport
            ? Math.max(0, window.visualViewport.offsetLeft || 0)
            : 0;
        const visualViewportTop = window.visualViewport
            ? Math.max(0, window.visualViewport.offsetTop || 0)
            : 0;
        const visibleViewportRight = Math.max(
            320,
            Math.min(
                window.innerWidth || Number.POSITIVE_INFINITY,
                document.documentElement.clientWidth || Number.POSITIVE_INFINITY,
                window.visualViewport
                    ? window.visualViewport.offsetLeft + window.visualViewport.width
                    : Number.POSITIVE_INFINITY,
            ),
        );
        const visibleViewportBottom = Math.max(
            240,
            Math.min(
                viewportHeight,
                document.documentElement.clientHeight || Number.POSITIVE_INFINITY,
                window.visualViewport
                    ? window.visualViewport.offsetTop + window.visualViewport.height
                    : Number.POSITIVE_INFINITY,
            ),
        );
        const anchorRect = anchor.getBoundingClientRect();
        const panelWidth = Math.round(measuredWidth || panel.getBoundingClientRect().width || 0);
        const anchorWidth = Math.round(anchorRect.width || 0);
        const edgeInset = 12;
        const widthSource = getStringAttribute(this, "panel-min-width");
        const maxWidth = visibleViewportRight - visualViewportLeft - edgeInset * 2;
        const width = Math.max(
            0,
            Math.min(
                widthSource === "panel" ? Math.max(anchorWidth, panelWidth) : (anchorWidth || panelWidth),
                Math.max(0, maxWidth),
            ),
        );
        this._clearDetachedResultsStyles();

        panel.style.position = "fixed";
        panel.style.margin = "0";
        panel.style.zIndex = "70";
        panel.style.width = width ? `${width}px` : "";
        panel.style.minWidth = "0";
        panel.style.maxWidth = `${Math.max(maxWidth, 160)}px`;
        panel.style.maxHeight = "";
        panel.style.overscrollBehavior = "";
        panel.style.left = `${visualViewportLeft + edgeInset}px`;
        panel.style.top = `${visualViewportTop + edgeInset}px`;

        const panelRect = panel.getBoundingClientRect();
        const minLeft = visualViewportLeft + edgeInset;
        let left = Math.max(minLeft, anchorRect.left);
        if (left + panelRect.width > visibleViewportRight - edgeInset) {
            left = Math.max(
                minLeft,
                visibleViewportRight - panelRect.width - edgeInset,
            );
        }

        const viewportBottom = Math.max(
            visualViewportTop + edgeInset + 1,
            visibleViewportBottom - edgeInset,
        );
        const minTop = visualViewportTop + edgeInset;
        const gap = 8;
        const topInViewport = Math.max(minTop, Math.round(anchorRect.bottom + gap));
        const naturalHeight = Math.round(panelRect.height || 0);
        const availableHeight = Math.max(0, viewportBottom - topInViewport);

        panel.style.left = `${left}px`;
        panel.style.top = `${topInViewport}px`;
        this._constrainPanelHeight(
            panel,
            Math.min(naturalHeight || availableHeight, availableHeight),
        );
    }

    _constrainPanelHeight(panel, maxHeight) {
        const constrainedHeight = Math.floor(Number(maxHeight) || 0);
        if (!(panel instanceof HTMLElement) || constrainedHeight <= 0) {
            return;
        }

        const results = this.resultsElement();
        const panelRect = panel.getBoundingClientRect();
        if (results instanceof HTMLElement) {
            const resultsRect = results.getBoundingClientRect();
            const reservedHeight = Math.max(
                0,
                Math.round((panelRect.height || 0) - (resultsRect.height || 0)),
            );
            const resultsMaxHeight = Math.max(48, constrainedHeight - reservedHeight);
            results.style.maxHeight = `${resultsMaxHeight}px`;
            results.style.overflowY = "auto";
            results.style.overscrollBehavior = "contain";
        }

        panel.style.maxHeight = `${constrainedHeight}px`;
        panel.style.overscrollBehavior = "contain";
    }

    _restorePanel() {
        const panel = this.panelElement();
        if (!(panel instanceof HTMLElement) || !this._panelDetached) {
            return;
        }
        this._detachPanelEvents();
        this._clearDetachedPanelStyles();
        if (this._panelAnchor?.parentNode) {
            this._panelAnchor.parentNode.insertBefore(panel, this._panelAnchor.nextSibling);
        } else {
            this.appendChild(panel);
        }
        this._panelDetached = false;
    }

    _usesDetachedPanel() {
        return getStringAttribute(this, "panel-mode") === "detached";
    }

    _scheduleClose() {
        this._cancelClose();
        this._closeTimer = window.setTimeout(() => {
            this._closeTimer = 0;
            this.close();
        }, CLOSE_DELAY_MS);
    }

    _syncSelection() {
        const selection = this.selectionElement();
        if (!(selection instanceof HTMLElement)) {
            return;
        }

        const selectedValues = this.selectedValues();
        selection.hidden = selectedValues.length === 0;

        selection.replaceChildren(
            ...selectedValues.map((value) => {
                const template = this._findCatalogTemplateByValue(value);
                const label = String(
                    template?.getAttribute("data-label") ?? value,
                ).trim();

                const chip = document.createElement("div");
                chip.className = "join items-stretch rounded-box border border-base-300 bg-base-100 p-1 text-base-content shadow-sm";

                const content = document.createElement("span");
                content.className = "inline-flex min-w-0 items-center px-2 py-1 text-sm";
                if (template instanceof HTMLTemplateElement) {
                    content.append(...cloneChildNodes(template.content));
                } else {
                    content.textContent = label;
                }
                chip.append(content);

                const removeButton = document.createElement("button");
                removeButton.type = "button";
                removeButton.className = "btn btn-ghost btn-xs btn-circle join-item h-7 min-h-0 w-7 border-0 text-base-content/70";
                removeButton.dataset.searchableMultiselectRemove = "";
                removeButton.setAttribute("data-value", value);
                const removeLabel = `Remove ${label}`;
                removeButton.setAttribute("aria-label", removeLabel);
                removeButton.setAttribute("data-fishy-tooltip", removeLabel);
                removeButton.setAttribute("data-fishy-tooltip-mode", "anchor-only");
                removeButton.textContent = "×";
                chip.append(removeButton);
                return chip;
            }),
        );
        this._positionOpenDetachedPanel();
    }

    _syncUi() {
        const searchInput = this.searchInputElement();
        if (searchInput instanceof HTMLInputElement && this.hasAttribute("placeholder")) {
            searchInput.placeholder = this.placeholder;
        }
    }

}

export function registerSearchableMultiselect() {
    if (window.customElements.get("fishy-searchable-multiselect")) {
        return;
    }
    window.customElements.define(
        "fishy-searchable-multiselect",
        FishySearchableMultiselect,
    );
}
